import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBranding } from './useBranding';
import {
  Select,
  MenuItem,
  FormControl,
  IconButton,
  TextField,
  Box,
  Menu,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Autosuggest from 'react-autosuggest';
import ThaiFlag from './thai-flag.png';
import EnglishFlag from './english-flag.png';
import ChinaFlag from './china-flag.png';
import SearchIcon from '@mui/icons-material/Search';
import Typesense from 'typesense';
import { debounce } from 'lodash';
import axios from 'axios';
import { ExclaimButtonStyles } from './styles';
import { useAuth } from '../Login/AuthContext';
import { useNavigate } from 'react-router-dom';
import menuItems from '../../utils/menuConfig';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FeedbackIcon from '@mui/icons-material/Feedback';
import { NAV_TABS } from '../../utils/categoriesConfig';
import { SEARCH_CATEGORIES } from '../../config/searchCategoriesConfig';
import { onExplicitUiLangSwitch } from '../../services/langPrefs';
import { useBehaviorTracking } from '../../hooks/useBehaviorTracking';
import CategorySelectionModal from '../InputPages/CategorySelectionModal';

const autosuggestStyles = {
  container: {
    position: 'relative',
    width: '100%',
    height: '40px'
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    paddingRight: '32px', 
    borderRadius: '20px',
    border: '1px solid #ccc',
    fontSize: '16px',
    backgroundColor: '#ffffff',
    height: '40px',
    boxSizing: 'border-box'
  },
  suggestionsContainer: {
    display: 'block',
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    maxHeight: '200px',
    overflowY: 'auto'
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none'
  },
  suggestion: {
    padding: '8px 12px',
    cursor: 'pointer',
    color: 'black'
  },
  suggestionHighlighted: {
    backgroundColor: '#ddd',
    color: 'black'
  }
};

// Improved validation of environment variables
const TYPESENSE_HOST = process.env.REACT_APP_TYPESENSE_HOST;
const TYPESENSE_PORT = parseInt(process.env.REACT_APP_TYPESENSE_PORT, 10);
const TYPESENSE_PROTOCOL = process.env.REACT_APP_TYPESENSE_PROTOCOL;
const TYPESENSE_API_KEY = process.env.REACT_APP_TYPESENSE_API_KEY;

// Create the Typesense client
let typesenseClient;
try {
  if (TYPESENSE_HOST && TYPESENSE_PORT && TYPESENSE_PROTOCOL && TYPESENSE_API_KEY) {
    typesenseClient = new Typesense.Client({
      nodes: [
        {
          host: TYPESENSE_HOST,
          port: TYPESENSE_PORT,
          protocol: TYPESENSE_PROTOCOL
        }
      ],
      apiKey: TYPESENSE_API_KEY,
      connectionTimeoutSeconds: 5,
      retryIntervalSeconds: 0.1,
      numRetries: 3
    });
    console.log('✅ Typesense client initialized successfully');
  } else {
    console.warn('⚠️ Typesense environment variables missing');
    typesenseClient = null;
  }
} catch (error) {
  console.error('❌ Failed to initialize Typesense client:', error);
  typesenseClient = null;
}

// Helper function to ensure dates are properly formatted as strings
const formatDateToString = (date) => {
  if (!date) return '';
  if (typeof date === 'string') return date;
  if (date instanceof Date) {
    try {
      return date.toISOString().split('T')[0];
    } catch (e) {
      console.error('Error converting date to string:', e);
      return '';
    }
  }
  return String(date);
};

const Header = ({
  setCoordinates,
  type,
  setType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  updatePlaces,
  mapBounds,
  onSearch,
  categoryFilter,      // NEW: raw dropdown state from Home.js
  setCategoryFilter,   // NEW: setter for dropdown state
}) => {
  const { language, changeLanguage } = useLanguage();
  const { isAuthenticated, currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [isShowingSuggestions, setIsShowingSuggestions] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [exclaimAnchor, setExclaimAnchor] = useState(null);
  const isExclaimOpen = Boolean(exclaimAnchor);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);

  // Behavior tracking for search
  const { trackSearch } = useBehaviorTracking('search');

  // AbortController for cleanup
  const abortControllerRef = useRef(null);

  // Calculate default dates - start from 1 Jan 2025, end 300 days later
  const defaultStart = '2025-01-01';
  const defaultStartDate = new Date('2025-01-01');
  const defaultEnd = new Date(defaultStartDate.getTime() + 300 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  // Ensure startDate and endDate are always strings
  const startDateString = useMemo(() => formatDateToString(startDate), [startDate]);
  const endDateString = useMemo(() => formatDateToString(endDate), [endDate]);

  // Initialize default values immediately on mount
  useEffect(() => {
    console.log('[🔄 INITIALIZATION] Setting up default values...');
    
    let needsUpdate = false;
    
    if (!startDate && setStartDate) {
      console.log('[📅 INIT] Setting default start date:', defaultStart);
      setStartDate(defaultStart);
      needsUpdate = true;
    }
    
    if (!endDate && setEndDate) {
      console.log('[📅 INIT] Setting default end date:', defaultEnd);
      setEndDate(defaultEnd);
      needsUpdate = true;
    }
    
    if (!type && setType) {
      console.log('[🏷️ INIT] Setting default type: Category');
      setType('Category');
      needsUpdate = true;
    }
    
    // Mark as initialized after a short delay to ensure all state updates are processed
    if (!isInitialized) {
      setTimeout(() => {
        console.log('[✅ INIT] Header initialization complete');
        setIsInitialized(true);
      }, 100);
    }
    
    console.log('[🔍 INIT DEBUG] Current state:', {
      startDate: startDate || 'undefined',
      endDate: endDate || 'undefined', 
      type: type || 'undefined',
      language: language || 'undefined',
      typesenseAvailable: !!typesenseClient,
      needsUpdate
    });
  }, []); // Run only once on mount


  // Build event types from centralized search category config
  const eventTypes = useMemo(() => {
    return [
      { value: 'Category', label: { TH: 'หมวดหมู่', EN: 'Category', CN: '类别' } },
      ...SEARCH_CATEGORIES,
    ];
  }, []);

  // Menu configuration and handlers for exclamation mark button
  // Debug: Log auth state to help troubleshoot menu issues
  useEffect(() => {
    console.log('[HEADER] Auth state:', {
      isAuthenticated,
      currentUser: currentUser?.name,
      role: currentUser?.role
    });
  }, [isAuthenticated, currentUser]);

  const menuConfig = menuItems(isAuthenticated, language);

  const handleExclaimOpen = (e) => setExclaimAnchor(e.currentTarget);
  const handleExclaimClose = () => setExclaimAnchor(null);

  // Handle menu item clicks with special actions
  const handleMenuItemClick = async (item) => {
    handleExclaimClose();

    // Handle special actions
    if (item.action === 'dashboard') {
      // Check if user is guest or not authenticated
      if (!isAuthenticated || !currentUser || currentUser.role === 'guest') {
        // Save returnUrl and redirect to login
        localStorage.setItem('returnUrl', '/dashboard');
        navigate('/login');
        return;
      }
      // User is authenticated and not a guest - go to dashboard
      navigate('/dashboard');
      return;
    }

    if (item.action === 'logout') {
      await logout();
      navigate('/login');
      return;
    }

    if (item.action === 'add-data') {
      setCategoryModalOpen(true);
      return;
    }

    // Handle regular navigation
    if (item.external) {
      window.open(item.path, '_blank', 'noopener');
    } else {
      navigate(item.path);
    }
  };

  const getMenuItemIcon = (label) => {
    const l = (label || '').toLowerCase();
    if (l.includes('scan') || l.includes('qr') || l.includes('扫描') || l.includes('สแกน')) {
      return <QrCodeScannerIcon />;
    }
    if (l.includes('tvent') || l.includes('旅游团') || l.includes('ทัวร์กลุ่ม')) {
      return <PeopleAltIcon />;
    }
    if (l.includes('event') || l.includes('กิจกรรม') || l.includes('活动') || l.includes('adding') || l.includes('添加') || l.includes('เพิ่ม')) {
      return <PlaylistAddIcon />;
    }
    if (l.includes('dashboard') || l.includes('แดชบอร์ด') || l.includes('仪表板')) {
      return <DashboardIcon />;
    }
    if (l.includes('logout') || l.includes('ออกจากระบบ') || l.includes('退出')) {
      return <LogoutIcon />;
    }
    if (l.includes('login') || l.includes('เข้าสู่ระบบ') || l.includes('登录') || l.includes('sign up') || l.includes('注册') || l.includes('สมัครสมาชิก')) {
      return <LoginIcon />;
    }
    return null;
  };

  const clearSearchTerm = () => {
    setSearchTerm('');
    setSuggestions([]);
    setIsShowingSuggestions(false);
    if (onSearch) onSearch('');
  };

  const getMapRadiusFilter = (bounds) => {
    if (!bounds?.sw || !bounds?.ne ||
        bounds.sw.lat == null || bounds.sw.lng == null ||
        bounds.ne.lat == null || bounds.ne.lng == null) {
      return null;
    }
    
    const centerLat = (bounds.sw.lat + bounds.ne.lat) / 2;
    const centerLng = (bounds.sw.lng + bounds.ne.lng) / 2;

    const toRad = (deg) => (deg * Math.PI) / 180;
    const earthRadius = 6371;

    const dLat = toRad(bounds.ne.lat - bounds.sw.lat);
    const dLng = toRad(bounds.ne.lng - bounds.sw.lng);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(bounds.sw.lat)) *
        Math.cos(toRad(bounds.ne.lat)) *
        Math.sin(dLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const diagonalKm = earthRadius * c;

    const dynamicRadius = Math.max(5, Math.floor(diagonalKm * 0.5));
    
    return `${centerLat}, ${centerLng}, ${dynamicRadius} km`;
  };

  const buildFilters = useCallback(() => {
    const filters = [];
    
    if (language) {
      filters.push(`language:="${language}"`);
    }

    // Category filter
    if (type && type !== 'Category') {
      filters.push(`main_category:="${type}"`);
    }

    // Date filters - simplified logic
    if (startDateString && endDateString) {
      const start = Math.floor(new Date(startDateString).setHours(0, 0, 0, 0) / 1000);
      const end = Math.floor(new Date(endDateString).setHours(23, 59, 59, 999) / 1000);
      
      const dateConditions = [
        `(event_date_ts:>=${start} && event_date_ts:<=${end})`,
        `(event_end_date_ts:>=${start} && event_end_date_ts:<=${end})`,
        `(event_date_ts:<=${start} && event_end_date_ts:>=${end})`
      ];
      
      filters.push(`(${dateConditions.join(' || ')})`);
    }

    // Map radius filter - make optional for initial load
    if (mapBounds) {
      const mapRadiusFilter = getMapRadiusFilter(mapBounds);
      if (mapRadiusFilter) {
        filters.push(`location:(${mapRadiusFilter})`);
      }
    }

    return filters;
  }, [language, type, startDateString, endDateString, mapBounds]);

  const buildSearchParams = useCallback((query, isAutosuggest = false) => {
    const params = {
      q: query || '*',
      query_by: 'event_name,searchableContent',
      per_page: isAutosuggest ? 10 : 40,
      typo_tokens_threshold: 1,
      num_typos: 1,
      prefix: true,
      sort_by: '_text_match:desc'
    };

    const filters = buildFilters();

    if (filters.length) {
      params.filter_by = filters.join(' && ');
    }
    
    console.log('[🔍 SEARCH PARAMS]', {
      query: params.q,
      filters: params.filter_by || 'None',
      isAutosuggest,
      currentState: { type, language, startDateString, endDateString, hasBounds: !!mapBounds }
    });
    
    return params;
  }, [buildFilters, type, language, startDateString, endDateString, mapBounds]);

  const getSuggestions = useCallback(async (value) => {
    if (!value.trim()) {
      setSuggestions([]);
      setIsShowingSuggestions(false);
      return;
    }

    if (!typesenseClient) {
      console.error('[❌ SUGGESTION] Typesense client not available');
      setSuggestions([]);
      setIsShowingSuggestions(false);
      return;
    }
    
    setSearchError(null);
    const params = buildSearchParams(value, true);
    
    try {
      console.log('[🔍 SUGGESTION SEARCH] Params:', params);
      const res = await typesenseClient.collections('events').documents().search(params);
      console.log('[📊 SUGGESTION RESULT]', res);
      
      if (!res || !Array.isArray(res.hits)) {
        console.warn('[⚠️ SUGGESTION] Invalid response format');
        setSuggestions([]);
        setIsShowingSuggestions(false);
        return;
      }
      
      const hits = res.hits.map((hit) => hit.document).filter(Boolean);
      const seen = new Set();
      const unique = hits.filter((doc) => {
        const name = doc.event_name || '';
        const isDuplicate = seen.has(name);
        if (name && !isDuplicate) {
          seen.add(name);
          return true;
        }
        return false;
      });
      
      console.log(`[✅ SUGGESTIONS] ${unique.length} unique suggestions found`);
      setSuggestions(unique);
      setIsShowingSuggestions(unique.length > 0);
      
    } catch (error) {
      console.error('[❌ SUGGESTION ERROR]', error);
      setSuggestions([]);
      setSearchError('Failed to fetch suggestions');
      setIsShowingSuggestions(false);
    }
  }, [buildSearchParams]);

  const getSuggestionsDebounced = useMemo(() =>
    debounce((value) => {
      if (value.length < 2) {
        setSuggestions([]);
        setIsShowingSuggestions(false);
        return;
      }
      getSuggestions(value);
    }, 300),
  [getSuggestions]);

  useEffect(() => () => {
    if (getSuggestionsDebounced?.cancel) {
      getSuggestionsDebounced.cancel();
    }
  }, [getSuggestionsDebounced]);

  const handleSearch = useCallback(async (forceSearchTerm = null) => {
    const queryTerm = forceSearchTerm !== null ? forceSearchTerm : searchTerm;

    console.log('🔍 [SEARCH TRIGGERED]', {
      queryTerm,
      forceSearchTerm,
      searchTerm,
      hasOnSearchProp: !!onSearch,
      hasTypesenseClient: !!typesenseClient,
      hasUpdatePlaces: !!updatePlaces
    });

    // If onSearch prop is provided, use it instead of the internal search logic
    if (onSearch) {
      console.log('🔄 Header: Delegating search to onSearch prop:', queryTerm);
      onSearch(queryTerm);
      return;
    }

    // Fallback to original search logic if no onSearch prop provided
    // Cancel any previous search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this search
    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    if (!typesenseClient) {
      console.error('[❌ SEARCH ERROR] Typesense client not initialized');
      console.error('[❌ SEARCH ERROR] Check environment variables:');
      console.error('  REACT_APP_TYPESENSE_HOST:', process.env.REACT_APP_TYPESENSE_HOST);
      console.error('  REACT_APP_TYPESENSE_PORT:', process.env.REACT_APP_TYPESENSE_PORT);
      console.error('  REACT_APP_TYPESENSE_PROTOCOL:', process.env.REACT_APP_TYPESENSE_PROTOCOL);
      console.error('  REACT_APP_TYPESENSE_API_KEY:', process.env.REACT_APP_TYPESENSE_API_KEY ? '***SET***' : 'NOT SET');
      if (updatePlaces) updatePlaces([]);
      return;
    }

    try {
      console.log(`[🔍 SEARCH START] Query: "${queryTerm}"`);
      console.log(`[🔍 SEARCH STATE] type: "${type}", language: "${language}"`);
      console.log(`[🔍 SEARCH STATE] dates: ${startDateString} to ${endDateString}`);
      console.log(`[🔍 SEARCH STATE] mapBounds:`, mapBounds ? 'SET' : 'NOT SET');

      const searchParams = buildSearchParams(queryTerm);

      console.log('[📊 SEARCH PARAMS FULL]', {
        query: searchParams.q,
        query_by: searchParams.query_by,
        filters: searchParams.filter_by || '❌ NO FILTERS',
        per_page: searchParams.per_page,
        sort_by: searchParams.sort_by
      });

      // Perform Typesense search
      const result = await typesenseClient.collections('events').documents().search(searchParams);

      // Check if search was aborted
      if (currentController.signal.aborted) {
        console.log('[🚫 SEARCH ABORTED] Search was cancelled');
        return;
      }

      console.log('[📈 SEARCH RESULTS]', {
        found: result.found || 0,
        hits: result.hits?.length || 0,
        facet_counts: result.facet_counts || 'None'
      });

      // Debug: Log event IDs returned
      const returnedIds = result.hits?.map(hit => hit.document?.id) || [];
      console.log('[🔍 EVENT IDS RETURNED]', returnedIds.slice(0, 10));

      if (!result || !Array.isArray(result.hits)) {
        console.warn('[⚠️ SEARCH WARNING] Unexpected response format:', result);
        if (updatePlaces) updatePlaces([]);
        return;
      }

      // If no results and we have filters, try without category filter
      if (result.hits.length === 0 && searchParams.filter_by) {
        console.log('[🔄 RETRY] No results with filters, trying basic search...');

        const basicParams = {
          q: queryTerm || '*',
          query_by: 'event_name,searchableContent',
          per_page: 40,
          filter_by: language ? `language:"${language}"` : undefined
        };

        console.log('[🔄 RETRY PARAMS]', basicParams);

        const retryResult = await typesenseClient.collections('events').documents().search(basicParams);

        // Check if search was aborted during retry
        if (currentController.signal.aborted) {
          console.log('[🚫 RETRY ABORTED] Retry search was cancelled');
          return;
        }

        console.log('[🔄 RETRY RESULTS]', {
          found: retryResult.found || 0,
          hits: retryResult.hits?.length || 0
        });

        if (retryResult && retryResult.hits && retryResult.hits.length > 0) {
          const ids = retryResult.hits.map(hit => {
            const id = hit.document.id;
            const numId = typeof id === 'string' ? parseInt(id, 10) : id;
            return isNaN(numId) ? null : numId;
          }).filter(id => id !== null);

          console.log(`🔍 Retry: Extracted ${ids.length} valid event IDs`);

          if (ids.length > 0) {
            const apiUrl = `${process.env.REACT_APP_API_URL}/events/batch`;
            const backendResponse = await axios.post(apiUrl, {
              eventIds: ids,
              language: language
            });

            if (!currentController.signal.aborted && updatePlaces) {
              updatePlaces(backendResponse.data);

              // Track retry search
              trackSearch(queryTerm, {
                category: type !== 'Category' ? type : null,
                startDate: startDateString,
                endDate: endDateString,
                language,
                retry: true
              }, backendResponse.data);
            }
            return;
          }
        }
      }

      const ids = result.hits.map(hit => {
        const id = hit.document.id;
        const numId = typeof id === 'string' ? parseInt(id, 10) : id;
        return isNaN(numId) ? null : numId;
      }).filter(id => id !== null);

      console.log(`🔍 Extracted ${ids.length} valid event IDs:`, ids.slice(0, 5));

      if (ids.length === 0) {
        console.log('[⚠️ NO EVENTS] No matching events found');
        if (!currentController.signal.aborted && updatePlaces) updatePlaces([]);
        return;
      }

      // Fetch from backend
      const apiUrl = `${process.env.REACT_APP_API_URL}/events/batch`;
      const backendResponse = await axios.post(apiUrl, {
        eventIds: ids,
        language: language
      });

      // Final check if search was aborted
      if (!currentController.signal.aborted) {
        console.log(`[✅ BACKEND SUCCESS] Received ${backendResponse.data.length} events from backend`);
        if (updatePlaces) updatePlaces(backendResponse.data);

        // Track successful search
        trackSearch(queryTerm, {
          category: type !== 'Category' ? type : null,
          startDate: startDateString,
          endDate: endDateString,
          language
        }, backendResponse.data);
      } else {
        console.log('[🚫 FINAL ABORTED] Search completed but results discarded');
      }

    } catch (error) {
      if (error.name === 'AbortError' || currentController.signal.aborted) {
        console.log('[🚫 SEARCH CANCELLED] Search was aborted');
        return;
      }

      console.error('[💥 SEARCH CRASH]', error.response?.data || error.message);
      if (updatePlaces) updatePlaces([]);
    }
  }, [typesenseClient, searchTerm, buildSearchParams, language, updatePlaces, onSearch, trackSearch, type, startDateString, endDateString]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // MAIN SEARCH TRIGGER - Only run when onSearch is NOT provided (fallback mode)
  useEffect(() => {
    // If onSearch prop is provided, skip automatic search - Home.js handles it
    if (onSearch) {
      console.log('[🔄 Header] onSearch prop provided, skipping automatic search');
      return;
    }

    if (!isInitialized) {
      console.log('[⏳ WAITING] Not initialized yet, skipping search');
      return;
    }

    if (!type || !language || !startDateString || !endDateString) {
      console.log('[⏳ WAITING] Missing required state for search:', {
        type: !!type,
        language: !!language,
        startDate: !!startDateString,
        endDate: !!endDateString
      });
      return;
    }

    console.log('[🔄 AUTO SEARCH] Running search with complete state:', {
      type,
      language,
      startDateString,
      endDateString,
      hasBounds: !!mapBounds,
      searchTerm: searchTerm || 'empty'
    });

    // Small delay to ensure all state updates are processed
    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [isInitialized, type, startDateString, endDateString, language, mapBounds, handleSearch, onSearch]);

  // Expose debug functions
  useEffect(() => {
    window.debugSearch = handleSearch;
    window.debugFilters = buildFilters;
    console.log('[🛠️ DEBUG] Search functions exposed: window.debugSearch(), window.debugFilters()');
  }, [handleSearch, buildFilters]);
  
  const onSuggestionSelected = (event, { suggestion }) => {
    if (suggestion) {
      const eventName = suggestion.displayed_name || suggestion.event_name || '';
      setSearchTerm(eventName); 
      setIsShowingSuggestions(false); 
      setTimeout(() => handleSearch(), 0); 
    }
  };

  const onSuggestionsFetchRequested = ({ value }) => {
    try {
      getSuggestionsDebounced(value);
    } catch (error) {
      console.error('[❌ SUGGESTION FETCH ERROR]', error);
      setSuggestions([]);
      setIsShowingSuggestions(false);
    }
  };

  const onSuggestionsClearRequested = () => {
    // Keep suggestions visible for now
  };

  const renderSuggestion = (suggestion) => {
    const displayName = suggestion.displayed_name || suggestion.event_name || '';
    return <div style={autosuggestStyles.suggestion}>{displayName}</div>;
  };

  const getSuggestionValue = (suggestion) => {
    return suggestion.displayed_name || suggestion.event_name || '';
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;
    console.log('[🏷️ CATEGORY DROPDOWN CHANGE]', value);
    if (setCategoryFilter) {
      setCategoryFilter(value === 'Category' ? '' : value);
    }
  };

  const handleStartDateChange = (valueOrEvent) => {
    let dateString;
    if (valueOrEvent?.target) {
      // Native input (mobile)
      dateString = valueOrEvent.target.value;
    } else if (valueOrEvent) {
      // MUI DatePicker (desktop)
      dateString = valueOrEvent.toISOString().split('T')[0];
    }
    if (dateString) {
      console.log('[📅 START DATE]', dateString);
      if (setStartDate) setStartDate(dateString);
    }
  };

  const handleEndDateChange = (valueOrEvent) => {
    let dateString;
    if (valueOrEvent?.target) {
      // Native input (mobile)
      dateString = valueOrEvent.target.value;
    } else if (valueOrEvent) {
      // MUI DatePicker (desktop)
      dateString = valueOrEvent.toISOString().split('T')[0];
    }
    if (dateString) {
      console.log('[📅 END DATE]', dateString);
      if (setEndDate) setEndDate(dateString);
    }
  };

  const handleLanguageChange = (newLanguage) => {
    console.log('[🌐 LANGUAGE]', newLanguage);
    changeLanguage(newLanguage);                         // updates context used by search filters
    onExplicitUiLangSwitch(newLanguage);                 // make it the primary fetch language immediately
    setTimeout(() => handleSearch(''), 0);               // instant refetch using the new language filter
  };


  return (
    <Box sx={{ 
      position: 'absolute', 
      top: 16, 
      left: 16, 
      zIndex: 11, 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: 1, 
      alignItems: 'center' 
    }}>
      
      <Box sx={{ width: 300, position: 'relative' }}> 
        
        {/* Debug red box removed as requested */}
        
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          onSuggestionSelected={onSuggestionSelected}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={{
            placeholder: language === 'TH' ? 'ค้นหากิจกรรม' : language === 'CN' ? '搜索活动' : 'Search for events',
            value: searchTerm,
            onChange: (_, { newValue }) => {
              setSearchTerm(newValue);
              if (newValue === '') {
                setSuggestions([]);
                setIsShowingSuggestions(false);
                if (onSearch) onSearch('');
              }
            },
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                setSuggestions([]);
                setIsShowingSuggestions(false);
                handleSearch();
              }
            },
            style: autosuggestStyles.input,
            'aria-label': 'Search events'
          }}
          theme={{
            ...autosuggestStyles,
            suggestionsContainer: {
              ...autosuggestStyles.suggestionsContainer,
              display: isShowingSuggestions && suggestions.length > 0 ? 'block' : 'none',
            }
          }}
          highlightFirstSuggestion={true}
          alwaysRenderSuggestions={false} 
        />
        
        {searchTerm && (
          <IconButton
            size="small"
            onClick={clearSearchTerm}
            sx={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888',
              padding: '2px',
              zIndex: 2 
            }}
            aria-label="Clear search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </IconButton>
        )}
        
        {searchError && <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.45 }}>{searchError}</Box>}
      </Box>

      <FormControl sx={{ 
        minWidth: 150, 
        bgcolor: 'white', 
        borderRadius: '20px',
        height: '40px'
      }}>
        <Select
          value={categoryFilter || 'Category'}
          onChange={handleTypeChange}
          size="small"
          fullWidth
          sx={{
            borderRadius: '20px',
            height: '40px'
          }}
        >
          {eventTypes.map(cat => (
            <MenuItem key={cat.value} value={cat.value}>
              {cat.label[language] || cat.label.EN}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Start Date - Desktop uses MUI DatePicker, Mobile uses native */}
      {isMobile ? (
        <TextField
          value={startDateString}
          onChange={handleStartDateChange}
          type="date"
          size="small"
          sx={{
            width: 145,
            bgcolor: 'white',
            borderRadius: '20px',
            '& .MuiInputBase-root': {
              height: '40px'
            }
          }}
          InputLabelProps={{ shrink: true }}
        />
      ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            value={startDateString ? new Date(startDateString) : null}
            onChange={handleStartDateChange}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{
                  width: 145,
                  bgcolor: 'white',
                  borderRadius: '20px',
                  '& .MuiInputBase-root': {
                    height: '40px'
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
      )}

      {/* End Date - Desktop uses MUI DatePicker, Mobile uses native */}
      {isMobile ? (
        <TextField
          value={endDateString}
          onChange={handleEndDateChange}
          type="date"
          size="small"
          sx={{
            width: 145,
            bgcolor: 'white',
            borderRadius: '20px',
            '& .MuiInputBase-root': {
              height: '40px'
            }
          }}
          InputLabelProps={{ shrink: true }}
        />
      ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DesktopDatePicker
            value={endDateString ? new Date(endDateString) : null}
            onChange={handleEndDateChange}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{
                  width: 145,
                  bgcolor: 'white',
                  borderRadius: '20px',
                  '& .MuiInputBase-root': {
                    height: '40px'
                  }
                }}
              />
            )}
          />
        </LocalizationProvider>
      )}

      <IconButton
        onClick={() => handleSearch()}
        sx={{
          bgcolor: '#1976d2',
          color: 'white',
          '&:hover': { bgcolor: '#1565c0' },
          height: 40,
          width: 40
        }}
        aria-label="Search"
      >
        <SearchIcon />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton
          onClick={() => handleLanguageChange('TH')}
          aria-label="Thai language"
          sx={{
            padding: '8px',
            border: language === 'TH' ? '2px solid #1976d2' : '2px solid transparent',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#f0f0f0'
            }
          }}
        >
          <img src={ThaiFlag} alt="TH" width={24} height={18} style={{ display: 'block' }} />
        </IconButton>
        <IconButton
          onClick={() => handleLanguageChange('EN')}
          aria-label="English language"
          sx={{
            padding: '8px',
            border: language === 'EN' ? '2px solid #1976d2' : '2px solid transparent',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#f0f0f0'
            }
          }}
        >
          <img src={EnglishFlag} alt="EN" width={24} height={18} style={{ display: 'block' }} />
        </IconButton>
        <IconButton
          onClick={() => handleLanguageChange('CN')}
          aria-label="Chinese language"
          sx={{
            padding: '8px',
            border: language === 'CN' ? '2px solid #1976d2' : '2px solid transparent',
            borderRadius: '8px',
            '&:hover': {
              backgroundColor: '#f0f0f0'
            }
          }}
        >
          <img src={ChinaFlag} alt="CN" width={24} height={18} style={{ display: 'block' }} />
        </IconButton>

        {/* Exclamation mark button placed next to CN flag */}
        <IconButton
          sx={ExclaimButtonStyles}
          aria-label="More options"
          onClick={handleExclaimOpen}
        >
          <ErrorOutlineIcon />
        </IconButton>
      </Box>

      {/* Exclamation Mark Dropdown Menu */}
      <Menu
        anchorEl={exclaimAnchor}
        open={isExclaimOpen}
        onClose={handleExclaimClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {menuConfig.map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => handleMenuItemClick(item)}
          >
            <ListItemIcon>{getMenuItemIcon(item.label)}</ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
        <MenuItem onClick={() => { handleExclaimClose(); window.open('https://flannel-chalk-c55.notion.site/347645f7a9f680978eafc56c0bdda911?pvs=105', '_blank', 'noopener'); }}>
          <ListItemIcon><FeedbackIcon /></ListItemIcon>
          <ListItemText primary="ส่งข้อเสนอแนะ" />
        </MenuItem>
      </Menu>

      {/* Category Selection Modal */}
      <CategorySelectionModal
        open={categoryModalOpen}
        onClose={() => setCategoryModalOpen(false)}
        language={language}
      />

    </Box>
  );
};

export default Header;